import { useState } from 'react';
import ApperIcon from '@/components/ApperIcon';
import GhostLoader from '@/components/GhostLoader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0">
        {eyebrow && (
          <p className="text-[11px] uppercase tracking-[0.22em] text-primary">{eyebrow}</p>
        )}
        <h1 className="mt-1 font-heading text-3xl leading-tight md:text-4xl">{title}</h1>
        {description && (
          <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  const { t } = useT();
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-12 text-center">
      <ApperIcon name="TriangleAlert" size={28} className="text-destructive" />
      <p className="mt-3 font-medium">{t('The signal dropped.')}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        {message ? t(message) : t('Something went wrong while loading.')}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={() => onRetry()}>
          <ApperIcon name="RotateCw" size={16} />
          {t('Try again')}
        </Button>
      )}
    </div>
  );
}

export function StatusBadge({ value, styles, className }) {
  const { t } = useT();
  if (value === null || value === undefined || value === '') return null;
  return (
    <Badge
      variant="outline"
      className={cn('border', styles?.[value] ?? 'border-border bg-muted text-muted-foreground', className)}
    >
      {t(String(value))}
    </Badge>
  );
}

export function Field({ label, htmlFor, hint, children, className }) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const isBlank = (v) => v === null || v === undefined || v === '' || v === false;

export function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-[8.5rem_1fr] gap-3 py-2.5 text-sm sm:grid-cols-[11rem_1fr]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">
        {isBlank(children) ? <span className="text-muted-foreground">—</span> : children}
      </dd>
    </div>
  );
}

export function Panel({ title, icon, actions, children, className, bodyClassName }) {
  return (
    <section className={cn('rounded-lg border border-border bg-card text-card-foreground shadow-xs', className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            {icon && <ApperIcon name={icon} size={16} className="text-primary" />}
            {title}
          </h2>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cn('px-4 py-2', bodyClassName)}>{children}</div>
    </section>
  );
}

export function Pager({ page, total, pageSize, hasMore, onPage }) {
  const { t } = useT();
  const pages = Math.max(1, Math.ceil((total || 0) / pageSize));
  if (pages <= 1 && !hasMore && page === 1) return null;
  return (
    <div className="mt-4 flex items-center justify-between gap-3 text-sm text-muted-foreground">
      <span>
        {t('Page {page} of {pages}', { page, pages })}
        {total ? ` · ${t('{count} total', { count: total })}` : ''}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ApperIcon name="ChevronLeft" size={16} className="rtl:rotate-180" />
          {t('Previous')}
        </Button>
        <Button variant="outline" size="sm" disabled={!hasMore && page >= pages} onClick={() => onPage(page + 1)}>
          {t('Next')}
          <ApperIcon name="ChevronRight" size={16} className="rtl:rotate-180" />
        </Button>
      </div>
    </div>
  );
}

export function Busy({ busy, label, busyLabel }) {
  if (!busy) return label;
  return (
    <>
      <GhostLoader compact size={16} />
      {busyLabel}
    </>
  );
}

export function ConfirmDelete({ title, description, onConfirm, label = 'Delete', size = 'sm', variant = 'outline' }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function go(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const ok = await onConfirm();
      if (ok !== false) setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant={variant} size={size} className="text-destructive hover:text-destructive">
          <ApperIcon name="Trash2" size={16} />
          {t(label)}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{t('Cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={go}
            disabled={busy}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            <Busy busy={busy} label={t(label)} busyLabel={t('Deleting…')} />
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function Metric({ label, value, hint, icon, tone = 'text-primary' }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-card-foreground shadow-xs">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.16em] text-muted-foreground">
        <span>{label}</span>
        {icon && <ApperIcon name={icon} size={16} className={tone} />}
      </div>
      <p className="mt-2 font-heading text-3xl leading-none">{value}</p>
      {hint && <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
